import React, {Fragment, useEffect, useState} from 'react';

import MediaQuery, {useMediaQuery} from 'react-responsive';
import {css} from 'styled-components';

export const BootstrapMinMediumMediaQuery = (
  props: React.ComponentProps<typeof MediaQuery>
) => <MediaQuery {...props} minWidth={768} />;

export const BootstrapMinLargeMediaQuery = (
  props: React.ComponentProps<typeof MediaQuery>
) => <MediaQuery {...props} minWidth={992} />;

export const BootstrapMaxSmallMediaQuery = (props: any) => (
  <MediaQuery {...props} maxWidth={767} />
);

export const BootstrapMaxMediumMediaQuery = (props: any) => (
  <MediaQuery {...props} maxWidth={991} />
);

export const mediaBreakpoints = {
  //xs to sm
  sm: 576,
  //sm to md
  md: 768,
  // md to lg
  lg: 992,
  // lg to xl
  xl: 1200,
} as const;

interface Props {
  breakpoint: keyof typeof mediaBreakpoints;
  small?: React.ReactNode;
  large?: React.ReactNode;
}

export function Responsive({breakpoint, small = null, large = null}: Props) {
  return (
    <Fragment>
      {small != null ? (
        <MediaQuery maxWidth={mediaBreakpoints[breakpoint] - 1}>
          {small}
        </MediaQuery>
      ) : null}
      {large != null ? (
        <MediaQuery minWidth={mediaBreakpoints[breakpoint]}>{large}</MediaQuery>
      ) : null}
    </Fragment>
  );
}

export function useIsScreenLargerThan(
  breakpoint: keyof typeof mediaBreakpoints
): boolean {
  return !useMediaQuery({
    query: `(max-width: ${mediaBreakpoints[breakpoint] - 1}px)`,
  });
}

function screenSizeToString({
  isScreenLargerThanSmall,
  isScreenLargerThanMedium,
  isScreenLargerThanLarge,
}: {
  isScreenLargerThanSmall: boolean;
  isScreenLargerThanMedium: boolean;
  isScreenLargerThanLarge: boolean;
}) {
  if (
    !isScreenLargerThanSmall &&
    !isScreenLargerThanMedium &&
    !isScreenLargerThanLarge
  ) {
    return 'sm';
  }

  if (
    isScreenLargerThanSmall &&
    !isScreenLargerThanMedium &&
    !isScreenLargerThanLarge
  ) {
    return 'md';
  }

  if (
    isScreenLargerThanSmall &&
    isScreenLargerThanMedium &&
    !isScreenLargerThanLarge
  ) {
    return 'lg';
  }

  if (
    isScreenLargerThanSmall &&
    isScreenLargerThanMedium &&
    isScreenLargerThanLarge
  ) {
    return 'xl';
  }

  return null;
}

export function useScreenSize() {
  // A helper function that is able to indicate what is the current device screen
  // size and return it in ``'sm' | 'md' | 'lg' | 'xl' `` format

  const isScreenLargerThanSmall = useIsScreenLargerThan('sm');
  const isScreenLargerThanMedium = useIsScreenLargerThan('md');
  const isScreenLargerThanLarge = useIsScreenLargerThan('lg');

  const [currentScreenSize, setCurrentScreenSize] = useState<
    'sm' | 'md' | 'lg' | 'xl' | null
  >(
    screenSizeToString({
      isScreenLargerThanSmall,
      isScreenLargerThanMedium,
      isScreenLargerThanLarge,
    })
  );

  useEffect(() => {
    const computedScreenSize = screenSizeToString({
      isScreenLargerThanSmall,
      isScreenLargerThanMedium,
      isScreenLargerThanLarge,
    });

    if (computedScreenSize != null) {
      setCurrentScreenSize(computedScreenSize);
    }
  }, [
    isScreenLargerThanSmall,
    isScreenLargerThanMedium,
    isScreenLargerThanLarge,
  ]);

  return {currentScreenSize};
}

export const styledMediaQuery = {
  only: (breakpoint: keyof typeof mediaBreakpoints) => {
    const keys = Object.keys(mediaBreakpoints) as Array<
      keyof typeof mediaBreakpoints
    >;
    const min = mediaBreakpoints[breakpoint];
    const maxIndex = keys.indexOf(breakpoint) + 1;
    const max =
      maxIndex < keys.length
        ? mediaBreakpoints[keys[maxIndex]] - 0.02
        : undefined;

    if (max !== undefined) {
      return (styles: TemplateStringsArray, ...args: any[]) => css`
        @media (min-width: ${min}px) and (max-width: ${max}px) {
          ${css(styles, ...args)}
        }
      `;
    }

    // No upper bound (e.g., 'xl')
    return (styles: TemplateStringsArray, ...args: any[]) => css`
      @media (min-width: ${min}px) {
        ${css(styles, ...args)}
      }
    `;
  },
};
